import * as React from 'react';
import { useState, useEffect, useMemo, Fragment } from 'react';
import { NT, NMChip, NI } from './NaomiTheme';
import { MSwitch } from './NaomiUi';
import type {
  SDM, TemplateVersion, ItemDef, ItemGroupDef, FormDef, CodeList, CodeListItem,
  ConditionDef, MethodDef, ItemRef, MeasurementUnit, OID, DataType,
} from './types/sdm';

export type AnswerValue = string | number | boolean | string[] | Record<string, string> | null;
export type AnswerMap   = Record<string, AnswerValue>;

export interface SDMHelpers {
  itemByOid:  Record<OID, ItemDef>;
  groupByOid: Record<OID, ItemGroupDef>;
  formByOid:  Record<OID, FormDef>;
  codeByOid:  Record<OID, CodeList>;
  condByOid:  Record<OID, ConditionDef>;
  methByOid:  Record<OID, MethodDef>;
}

export interface UseSDMStateResult extends SDMHelpers {
  sdm: SDM;
  setSdm: React.Dispatch<React.SetStateAction<SDM>>;
  tv: TemplateVersion;
  updateItem:     (oid: OID, patch: Partial<ItemDef>) => void;
  updateItemRef:  (groupOid: OID, itemOid: OID, patch: Partial<ItemRef>) => void;
  reorderItems:   (groupOid: OID, fromIdx: number, toIdx: number) => void;
  addItem:        (groupOid: OID, partial: Partial<ItemDef> & { text?: string }) => void;
  removeItem:     (groupOid: OID, itemOid: OID) => void;
}

function indexBy<T extends { OID: OID }>(items: T[]): Record<OID, T> {
  const out: Record<OID, T> = {};
  for (const it of items) out[it.OID] = it;
  return out;
}

export function useSDMState(seed: SDM): UseSDMStateResult {
  const [sdm, setSdm] = useState<SDM>(() => JSON.parse(JSON.stringify(seed)) as SDM);
  const tv = sdm.TemplateVersion[0];

  const helpers = useMemo<SDMHelpers>(() => ({
    itemByOid:  indexBy(tv.ItemDef),
    groupByOid: indexBy(tv.ItemGroupDef),
    formByOid:  indexBy(tv.FormDef),
    codeByOid:  indexBy(tv.CodeList),
    condByOid:  indexBy(tv.ConditionDef),
    methByOid:  indexBy(tv.MethodDef),
  }), [sdm, tv]);

  const mapTV = (s: SDM, fn: (v: TemplateVersion) => TemplateVersion): SDM => ({
    ...s,
    TemplateVersion: s.TemplateVersion.map((v, i) => i === 0 ? fn(v) : v),
  });

  const updateItem = (oid: OID, patch: Partial<ItemDef>): void => setSdm((s) =>
    mapTV(s, (v) => ({
      ...v,
      ItemDef: v.ItemDef.map((it) => it.OID === oid ? { ...it, ...patch } : it),
    })),
  );

  const updateItemRef = (groupOid: OID, itemOid: OID, patch: Partial<ItemRef>): void =>
    setSdm((s) => mapTV(s, (v) => ({
      ...v,
      ItemGroupDef: v.ItemGroupDef.map((g) => g.OID === groupOid
        ? { ...g, ItemRef: g.ItemRef.map((r) => r.ItemOID === itemOid ? { ...r, ...patch } : r) }
        : g,
      ),
    })));

  const reorderItems = (groupOid: OID, fromIdx: number, toIdx: number): void =>
    setSdm((s) => mapTV(s, (v) => ({
      ...v,
      ItemGroupDef: v.ItemGroupDef.map((g) => {
        if (g.OID !== groupOid) return g;
        const arr = g.ItemRef.slice();
        const [moved] = arr.splice(fromIdx, 1);
        arr.splice(toIdx, 0, moved);
        return { ...g, ItemRef: arr };
      }),
    })));

  const addItem = (groupOid: OID, partial: Partial<ItemDef> & { text?: string }): void =>
    setSdm((s) => {
      const cur = s.TemplateVersion[0];
      const nextN = Math.max(...cur.ItemDef.map((i) => parseInt(i.OID.slice(2), 10) || 0)) + 1;
      const oid: OID = `I.${nextN}`;
      const item: ItemDef = {
        OID: oid,
        Name: `Q${nextN}`,
        DataType: 'text',
        Question: { TranslatedText: { lang: 'en', text: partial.text ?? 'New question' } },
        ...partial,
      };
      return mapTV(s, (v) => ({
        ...v,
        ItemDef: [...v.ItemDef, item],
        ItemGroupDef: v.ItemGroupDef.map((g) => g.OID === groupOid
          ? { ...g, ItemRef: [...g.ItemRef, { ItemOID: oid, Mandatory: 'No' }] }
          : g,
        ),
      }));
    });

  const removeItem = (groupOid: OID, itemOid: OID): void =>
    setSdm((s) => mapTV(s, (v) => ({
      ...v,
      ItemGroupDef: v.ItemGroupDef.map((g) => g.OID === groupOid
        ? { ...g, ItemRef: g.ItemRef.filter((r) => r.ItemOID !== itemOid) }
        : g,
      ),
    })));

  return { sdm, setSdm, tv, ...helpers, updateItem, updateItemRef, reorderItems, addItem, removeItem };
}

export interface BranchBadgeProps { cond?: ConditionDef | null }
export function BranchBadge({ cond }: BranchBadgeProps): JSX.Element | null {
  if (!cond) return null;
  return (
    <NMChip color="logic" icon={<NI.Branch/>}>
      Show when: {cond.humanReadable ?? cond.FormalExpression.text}
    </NMChip>
  );
}

export interface CalcBadgeProps { method?: MethodDef | null }
export function CalcBadge({ method }: CalcBadgeProps): JSX.Element | null {
  if (!method) return null;
  const isRepeat = method.kind === 'repeat';
  return (
    <NMChip color={isRepeat ? 'warn' : 'calc'} icon={isRepeat ? <NI.Repeat/> : <NI.Calc/>}>
      {isRepeat ? 'Repeats: ' : 'Calculated: '}{method.humanReadable ?? method.FormalExpression.text}
    </NMChip>
  );
}

export interface TypeMetaEntry { icon: React.ReactNode; label: string; }

export const TypeMeta: Partial<Record<DataType, TypeMetaEntry>> = {
  text:         { icon: <NI.Text/>,   label: 'Short text' },
  integer:      { icon: <NI.Number/>, label: 'Number' },
  boolean:      { icon: <NI.Toggle/>, label: 'Boolean (Yes/No)' },
  singleselect: { icon: <NI.Radio/>,  label: 'Single choice' },
  multiselect:  { icon: <NI.Check2/>, label: 'Multi choice' },
};

export type LivePreviewExtras =
  Partial<Record<string, (item: ItemDef, value: AnswerValue, set: (v: AnswerValue) => void) => React.ReactNode>>;
export type GroupBannerRenderer = (group: ItemGroupDef) => React.ReactNode;

export type ValidationErrorRenderer = React.FC<{
  value: AnswerValue;
  validations: Record<string, unknown>;
  dataType: DataType;
}>;

interface PreviewGlobals {
  LivePreviewExtras?: LivePreviewExtras;
  renderGroupHeaderBanner?: GroupBannerRenderer;
  RenderValidationError?: ValidationErrorRenderer;
}

const NUMERIC_TYPES = new Set(['integer', 'float', 'currency', 'percent', 'feetinches']);

export function evalRangeCheck(rc: { Comparator: string; CheckValue: string; ErrorMessage?: { TranslatedText: { lang: string; text: string } } }, num: number): string | null {
  const cv = Number(rc.CheckValue);
  const violated =
    rc.Comparator === 'LT' ? num < cv :
    rc.Comparator === 'LE' ? num <= cv :
    rc.Comparator === 'GT' ? num > cv :
    rc.Comparator === 'GE' ? num >= cv :
    rc.Comparator === 'EQ' ? num === cv :
    rc.Comparator === 'NE' ? num !== cv : false;
  if (!violated) return null;
  return rc.ErrorMessage?.TranslatedText?.text || `Value must not be ${rc.Comparator} ${cv}.`;
}

function getValidationError(
  value: AnswerValue,
  validations: { required?: boolean; min?: number; max?: number; pattern?: string; message?: string; dateMin?: string; dateMax?: string },
  dataType: DataType,
  rangeChecks?: any[],
): string | null {
  const { required, min, max, pattern, message, dateMin, dateMax } = validations;

  if (required && (value === null || value === '' || value === undefined)) {
    return message || 'This field is required.';
  }

  if (NUMERIC_TYPES.has(dataType as string)) {
    const num = typeof value === 'object' && value !== null
      ? parseFloat(String((value as Record<string, string>).ft ?? ''))
      : parseFloat(String(value ?? ''));
    if (!isNaN(num)) {
      if (rangeChecks?.length) {
        for (const rc of rangeChecks) {
          const err = evalRangeCheck(rc, num);
          if (err) return err;
        }
      } else {
        if (min !== undefined && num < min) return message || `Value must be at least ${min}.`;
        if (max !== undefined && num > max) return message || `Value must be no more than ${max}.`;
      }
    }
  }

  if ((dataType === 'date' || dataType === 'datetime') && typeof value === 'string' && value !== '') {
    if (dateMin && value < dateMin) return message || `Must be on or after ${dateMin}`;
    if (dateMax && value > dateMax) return message || `Must be on or before ${dateMax}`;
  }

  if (pattern && typeof value === 'string' && value !== '') {
    try {
      if (!new RegExp(pattern).test(value)) return message || 'Invalid format.';
    } catch (err: unknown) {
      console.warn('[NaomiShared] Invalid regex pattern:', pattern, err instanceof Error ? err.message : err);
    }
  }

  return null;
}

function DefaultValidationError({ value, validations, dataType }: {
  value: AnswerValue;
  validations: Record<string, unknown>;
  dataType: DataType;
}): JSX.Element | null {
  const rcs: any[] | undefined = (validations as any)._rangeChecks;
  const err = getValidationError(value, validations as Parameters<typeof getValidationError>[1], dataType, rcs);
  if (!err) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 12, color: NT.danger }}>
      {err}
    </div>
  );
}

const SAMPLE_DEFAULTS: AnswerMap = {
  'I.1': '1428 Oakleaf Drive', 'I.2': 'Rodriguez Family', 'I.3': '1', 'I.4': 2,
  'I.10': 48, 'I.11': 32, 'I.12': 7, 'I.13': '', 'I.14': 0,
  'I.20': 1, 'I.21': 2, 'I.22': 3,
  'I.40': '2',
};

export interface LivePreviewProps {
  sdm: SDM;
  density?: 'normal' | 'dense';
  fillSample?: boolean;
  showAllErrors?: boolean;
  catalogueSasMap?: Record<string, string>;
}

function evalExpr(src: string, ctx: AnswerMap): unknown {
  let pos = 0;
  const ws  = (): void    => { while (pos < src.length && src[pos] <= ' ') pos++; };
  const eat = (s: string): boolean => { if (src.startsWith(s, pos)) { pos += s.length; return true; } return false; };

  function ternary(): unknown {
    const c = or(); ws();
    if (!eat('?')) return c;
    ws(); const t = ternary(); ws();
    if (!eat(':')) throw 0;
    ws(); return c ? t : ternary();
  }
  function or(): unknown {
    let v = and(); ws();
    while (eat('||')) { ws(); const r = and(); v = v || r; ws(); }
    return v;
  }
  function and(): unknown {
    let v = eq(); ws();
    while (eat('&&')) { ws(); const r = eq(); v = v && r; ws(); }
    return v;
  }
function eq(): unknown {
    let v = rel(); ws();
    for (;;) {
      if      (eat('===')) { ws(); v = v === rel(); }
      else if (eat('!==')) { ws(); v = v !== rel(); }
      else if (eat('=='))  { ws(); v = (v as any) == (rel() as any); }
      else if (eat('!='))  { ws(); v = (v as any) != (rel() as any); }
      else break;
      ws();
    }
    return v;
  }
  function rel(): unknown {
    let v = add(); ws();
    for (;;) {
      if      (eat('<=')) { ws(); v = (v as number) <= (add() as number); }
      else if (eat('>=')) { ws(); v = (v as number) >= (add() as number); }
      else if (eat('<'))  { ws(); v = (v as number) <  (add() as number); }
      else if (eat('>'))  { ws(); v = (v as number) >  (add() as number); }
      else break;
      ws();
    }
    return v;
  }
  function add(): unknown {
    let v = mul(); ws();
    for (;;) {
      if      (eat('+')) { ws(); v = (v as number) + (mul() as number); }
      else if (eat('-')) { ws(); v = (v as number) - (mul() as number); }
      else break;
      ws();
    }
    return v;
  }
  function mul(): unknown {
    let v = unary(); ws();
    for (;;) {
      if      (eat('*')) { ws(); v = (v as number) * (unary() as number); }
      else if (eat('/')) { ws(); v = (v as number) / (unary() as number); }
      else break;
      ws();
    }
    return v;
  }
  function unary(): unknown {
    ws();
    if (src.startsWith('!(', pos)) {
      const save = pos; pos += 2; ws();
      const m = /^I\.(\d+)/.exec(src.slice(pos));
      if (m) {
        pos += m[0].length; ws();
        if (eat(':')) {
          ws();
          if (src.startsWith('RepeatCount', pos) && !/\w/.test(src[pos + 11] ?? '')) {
            pos += 11; ws();
            if (eat(')')) return parseInt(String(ctx[`I.${m[1]}`] ?? ''), 10) || 0;
          }
        }
      }
      pos = save;
    }
    if (eat('!')) { ws(); return !unary(); }
    if (eat('-')) { ws(); return -(unary() as number); }
    return primary();
  }
  function primary(): unknown {
    ws();
    if (eat('(')) { const v = ternary(); ws(); if (!eat(')')) throw 0; return v; }
    const ch = src[pos];
    if (ch === '"' || ch === "'") {
      const q = src[pos++]; let s = '';
      while (pos < src.length && src[pos] !== q) { if (src[pos] === '\\') pos++; s += src[pos++]; }
      if (src[pos] === q) pos++;
      return s;
    }
    const oid = /^I\.(\d+)/.exec(src.slice(pos));
    if (oid) { pos += oid[0].length; return ctx[`I.${oid[1]}`] ?? null; }
    const num = /^\d+(\.\d+)?/.exec(src.slice(pos));
    if (num) { pos += num[0].length; return parseFloat(num[0]); }
    if (src.startsWith('true',  pos) && !/\w/.test(src[pos + 4]  ?? '')) { pos += 4;  return true;  }
    if (src.startsWith('false', pos) && !/\w/.test(src[pos + 5]  ?? '')) { pos += 5;  return false; }
    if (src.startsWith('null',  pos) && !/\w/.test(src[pos + 4]  ?? '')) { pos += 4;  return null;  }
    throw 0;
  }

  try {
    ws(); const result = ternary(); ws();
    return pos < src.length ? null : result;
  } catch { return null; }
}

export function LivePreview({
  sdm, density = 'normal', fillSample = false, showAllErrors = false, catalogueSasMap = {},
}: LivePreviewProps): JSX.Element {
  const tv = sdm.TemplateVersion[0];

  const resolveCatalogueImg = (relPathOrUrl?: string): string | undefined => {
    if (!relPathOrUrl) return undefined;
    if (relPathOrUrl.startsWith('http://') || relPathOrUrl.startsWith('https://') || relPathOrUrl.startsWith('data:')) return relPathOrUrl;
    return catalogueSasMap[relPathOrUrl];
  };
  const getOptionImg = (ci: CodeListItem): string | undefined =>
    resolveCatalogueImg(ci.ThumbnailUrl) || resolveCatalogueImg(ci.ImageUrl) || resolveCatalogueImg((ci as any).IconUrl);

  const itemByOid  = useMemo(() => indexBy(tv.ItemDef),       [tv]);
  const groupByOid = useMemo(() => indexBy(tv.ItemGroupDef),  [tv]);
  const formByOid  = useMemo(() => indexBy(tv.FormDef),       [tv]);
  const codeByOid  = useMemo(() => indexBy(tv.CodeList),      [tv]);
  const condByOid  = useMemo(() => indexBy(tv.ConditionDef),  [tv]);
  const methByOid  = useMemo(() => indexBy(tv.MethodDef),     [tv]);
  const muByOid    = useMemo<Record<OID, MeasurementUnit>>(
    () => indexBy(sdm.BasicDefinition.MeasurementUnit),
    [sdm.BasicDefinition.MeasurementUnit],
  );

  const [answers, setAnswers] = useState<AnswerMap>(() => fillSample ? { ...SAMPLE_DEFAULTS } : {});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [summarizeMode, setSummarizeMode] = useState<Record<string, boolean>>({});

  useEffect(() => { if (fillSample) setAnswers({ ...SAMPLE_DEFAULTS }); }, [fillSample]);
  useEffect(() => { if (showAllErrors) setSubmitted(true); }, [showAllErrors]);

  const markTouched = (k: string): void =>
    setTouched((t) => t[k] ? t : { ...t, [k]: true });

  const set = (oid: OID, v: AnswerValue, inst?: number): void =>
    setAnswers((a) => inst === undefined
      ? { ...a, [oid]: v }
      : { ...a, [`${oid}@${inst}`]: v },
    );
  const get = (oid: OID, inst?: number): AnswerValue =>
    inst === undefined ? answers[oid] ?? null : answers[`${oid}@${inst}`] ?? null;

  const isHidden = (conditionOID?: OID): boolean => {
    if (!conditionOID) return false;
    const c = condByOid[conditionOID];
    if (!c) return false;
    return evalExpr(c.FormalExpression.text, answers) === true;
  };

  const isItemHidden = (itemOid: OID): boolean => {
    for (const g of tv.ItemGroupDef) {
      const ref = g.ItemRef.find(r => r.ItemOID === itemOid);
      if (ref?.CollectionExceptionConditionOID) return isHidden(ref.CollectionExceptionConditionOID);
      for (const sg of (g.Subgroups ?? [])) {
        const sgRef = sg.ItemRef.find(r => r.ItemOID === itemOid);
        if (sgRef?.CollectionExceptionConditionOID) return isHidden(sgRef.CollectionExceptionConditionOID);
      }
    }
    return false;
  };

  const extractRepeatCountOid = (methodOID: OID): OID | null => {
    const m = methByOid[methodOID];
    if (!m) return null;
    const match = m.FormalExpression.text.match(/!\(\s*(I\.\d+)\s*:\s*RepeatCount\s*\)/);
    return match ? match[1] as OID : null;
  };

  const computeMethod = (methodOID: OID): unknown => {
    const m = methByOid[methodOID];
    if (!m) return null;
    return evalExpr(m.FormalExpression.text, answers);
  };

  useEffect(() => {
    const updates: AnswerMap = {};
    tv.ItemGroupDef.forEach((g) => g.ItemRef.forEach((ref) => {
      if (ref.MethodOID) {
        const m = methByOid[ref.MethodOID];
        const v = m ? evalExpr(m.FormalExpression.text, answers) : null;
        if (v != null && v !== answers[ref.ItemOID]) {
          updates[ref.ItemOID] = v as AnswerValue;
        }
      }
    }));
    if (Object.keys(updates).length) setAnswers((a) => ({ ...a, ...updates }));
  }, [answers, sdm, methByOid]);

  useEffect(() => {
    const toDelete: string[] = [];
    const checkRefs = (refs: ItemRef[]) => {
      for (const ref of refs) {
        if (ref.CollectionExceptionConditionOID) {
          const c = condByOid[ref.CollectionExceptionConditionOID];
          const hidden = c ? evalExpr(c.FormalExpression.text, answers) === true : false;
          if (hidden && answers[ref.ItemOID] != null) toDelete.push(ref.ItemOID);
        }
      }
    };
    for (const g of tv.ItemGroupDef) {
      checkRefs(g.ItemRef);
      for (const sg of (g.Subgroups ?? [])) checkRefs(sg.ItemRef);
    }
    if (!toDelete.length) return;
    setAnswers((a) => {
      const next = { ...a };
      toDelete.forEach((oid) => { delete next[oid]; });
      return next;
    });
  }, [answers, sdm, condByOid]);

  const dens = density === 'dense'
    ? { formPad: 14, gap: 8,  qGap: 10 }
    : { formPad: 20, gap: 14, qGap: 16 };

  const globals = (typeof window !== 'undefined' ? window as unknown as PreviewGlobals : {});

  const renderItem = (
    item: ItemDef | undefined,
    ref: ItemRef,
    groupOid: OID,
    inst?: number,
  ): React.ReactNode => {
    if (!item) return null;
    if (ref.IsVisible === 'No') return null;
    if (isHidden(ref.CollectionExceptionConditionOID)) return null;

    const mu = item.MeasurementOid ? muByOid[item.MeasurementOid] : undefined;
    const isCalc = !!ref.MethodOID;
    const instKey = inst !== undefined ? `@${inst}` : '';
    const tk = `${item.OID}${instKey}`;
    const value = get(item.OID, inst);
    const onBlur = (): void => markTouched(tk);

    let input: React.ReactNode;

    if (item.DataType === 'integer' || item.DataType === 'float') {
      const isInt = item.DataType === 'integer';
      input = (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            id={`naomi-shared-input-item-number-${item.OID}`}
            className="nm-input" type="number"
            step={isInt ? 1 : 'any'}
            value={value === null || value === undefined ? '' : String(value)}
            disabled={isCalc}
            onBlur={onBlur}
            onChange={(e) => {
              set(item.OID, e.target.value === '' ? '' : Number(e.target.value), inst);
              if (e.target.value !== '') markTouched(tk);
            }}
            style={{ flex: 1 }}
          />
          {mu && (
            <span style={{ alignSelf: 'center', padding: '0 10px', color: NT.ink3, fontWeight: 500, fontSize: 13 }}>
              {mu.Symbol}
            </span>
          )}
        </div>
      );
    } else if (item.DataType === 'text') {
      input = (
        <input id={`naomi-shared-input-item-text-${item.OID}`} className="nm-input"
          value={typeof value === 'string' ? value : ''}
          onBlur={onBlur}
          onChange={(e) => set(item.OID, e.target.value, inst)}/>
      );
    } else if (item.DataType === 'multilinetext') {
      input = (
        <textarea id={`naomi-shared-input-item-multilinetext-${item.OID}`} className="nm-input"
          value={typeof value === 'string' ? value : ''}
          rows={3}
          onBlur={onBlur}
          onChange={(e) => set(item.OID, e.target.value, inst)}
          style={{ resize: 'vertical', minHeight: 80 }}/>
      );
    } else if (item.DataType === 'boolean') {
      input = (
        <div style={{ display: 'flex', gap: 6 }}>
          {(['Yes', 'No'] as const).map(opt => {
            const sel = value === opt;
            return (
              <button id={`naomi-shared-button-item-boolean-${item.OID}-${opt}`} key={opt} type="button"
                onClick={() => { set(item.OID, sel ? null : opt, inst); markTouched(tk); }}
                style={{
                  padding: '8px 24px',
                  border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
                  background: sel ? NT.primarySoft : NT.surface,
                  borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  fontWeight: sel ? 600 : 400,
                  color: sel ? NT.primary2 : NT.ink2,
                }}>
                {opt}
              </button>
            );
          })}
        </div>
      );
    } else if (item.DataType === 'singleselect') {
      const cl = item.CodeListRef ? codeByOid[item.CodeListRef.CodeListOID] : undefined;
      const hasImages = cl?.CodeListItem.some((ci) => getOptionImg(ci));
      input = (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${hasImages ? 140 : 180}px, 1fr))`, gap: 6 }}>
          {cl?.CodeListItem.map((ci) => {
            const sel = value === ci.CodedValue;
            const img = getOptionImg(ci);
            return (
              <button id={`naomi-shared-button-item-option-${item.OID}-${ci.CodedValue}`} key={ci.CodedValue} type="button"
                onClick={() => { set(item.OID, sel ? null : ci.CodedValue, inst); markTouched(tk); }}
                style={{
                  padding: img ? '0' : '8px 10px', textAlign: 'left',
                  border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
                  background: sel ? NT.primarySoft : NT.surface,
                  borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  fontWeight: sel ? 600 : 400,
                  color: sel ? NT.primary2 : NT.ink2,
                  display: 'flex', flexDirection: img ? 'column' : 'row',
                  alignItems: img ? 'stretch' : 'center', gap: img ? 0 : 8,
                  overflow: 'hidden',
                }}>
                {img && (
                  <div style={{
                    width: '100%', height: 90, flexShrink: 0,
                    background: `url("${img}") center/cover no-repeat ${NT.surfaceAlt}`,
                  }}/>
                )}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: img ? '6px 8px' : '0',
                }}>
                  <span style={{
                    width: 14, height: 14, borderRadius: 7, flexShrink: 0,
                    border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {sel && <span style={{ width: 6, height: 6, borderRadius: 3, background: NT.primary }}/>}
                  </span>
                  {ci.Decode.TranslatedText.text}
                </div>
              </button>
            );
          })}
        </div>
      );
    } else if (item.DataType === 'multiselect') {
      const cl = item.CodeListRef ? codeByOid[item.CodeListRef.CodeListOID] : undefined;
      const arr: string[] = Array.isArray(value) ? value : [];
      const hasImages = cl?.CodeListItem.some((ci) => getOptionImg(ci));
      input = (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${hasImages ? 140 : 180}px, 1fr))`, gap: 6 }}>
          {cl?.CodeListItem.map((ci) => {
            const sel = arr.includes(ci.CodedValue);
            const img = getOptionImg(ci);
            return (
              <button id={`naomi-shared-button-item-option-${item.OID}-${ci.CodedValue}`} key={ci.CodedValue} type="button"
                onClick={() => {
                  const next = sel ? arr.filter((x) => x !== ci.CodedValue) : [...arr, ci.CodedValue];
                  set(item.OID, next, inst); markTouched(tk);
                }}
                style={{
                  padding: img ? '0' : '8px 10px', textAlign: 'left',
                  border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
                  background: sel ? NT.primarySoft : NT.surface,
                  borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  fontWeight: sel ? 600 : 400,
                  color: sel ? NT.primary2 : NT.ink2,
                  display: 'flex', flexDirection: img ? 'column' : 'row',
                  alignItems: img ? 'stretch' : 'center', gap: img ? 0 : 8,
                  overflow: 'hidden',
                }}>
                {img && (
                  <div style={{
                    width: '100%', height: 90, flexShrink: 0,
                    background: `url("${img}") center/cover no-repeat ${NT.surfaceAlt}`,
                  }}/>
                )}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: img ? '6px 8px' : '0',
                }}>
                  <span style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                    border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
                    background: sel ? NT.primary : 'transparent',
                    color: '#fff', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 800,
                  }}>{sel && '✓'}</span>
                  {ci.Decode.TranslatedText.text}
                </div>
              </button>
            );
          })}
        </div>
      );
    } else if (item.DataType === 'date') {
      input = (
        <input className="nm-input" type="date"
          value={typeof value === 'string' ? value : ''}
          onBlur={onBlur}
          onChange={(e) => { set(item.OID, e.target.value, inst); markTouched(tk); }}/>
      );
    } else if (item.DataType === 'feetinches') {
      const ftIn = (typeof value === 'object' && value !== null) ? value as { ft?: string; in?: string } : { ft: '', in: '' };
      input = (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input className="nm-input" type="number" min="0" placeholder="0"
            value={ftIn.ft ?? ''}
            onBlur={onBlur}
            onChange={(e) => { set(item.OID, { ...ftIn, ft: e.target.value }, inst); markTouched(tk); }}
            style={{ flex: 1 }}/>
          <span style={{ padding: '0 6px', color: NT.ink3, fontWeight: 600, fontSize: 13, flexShrink: 0 }}>ft</span>
          <input className="nm-input" type="number" min="0" max="11" placeholder="0"
            value={ftIn.in ?? ''}
            onBlur={onBlur}
            onChange={(e) => { set(item.OID, { ...ftIn, in: e.target.value }, inst); markTouched(tk); }}
            style={{ flex: 1 }}/>
          <span style={{ padding: '0 6px', color: NT.ink3, fontWeight: 600, fontSize: 13, flexShrink: 0 }}>in</span>
        </div>
      );
    } else {
      const ext = globals.LivePreviewExtras?.[item.DataType as string];
      input = ext
        ? ext(item, value, (v) => set(item.OID, v, inst))
        : <input id={`naomi-shared-input-item-text-${item.OID}`}  className="nm-input"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(item.OID, e.target.value, inst)}
            placeholder={item.DataType}/>;
    }

    const ValidationError = globals.RenderValidationError ?? DefaultValidationError;

    return (
      <div key={`${item.OID}${instKey}-${groupOid}`} className="nm-fade-in"
        style={{ marginBottom: dens.qGap }}>
        {(resolveCatalogueImg(item.ThumbnailUrl) || resolveCatalogueImg(item.ImageUrl)) && (
          <div style={{
            width: '100%', maxHeight: 180, marginBottom: 8, borderRadius: 6,
            overflow: 'hidden', border: `1px solid ${NT.line}`, background: NT.surfaceAlt,
          }}>
            <img src={resolveCatalogueImg(item.ThumbnailUrl) || resolveCatalogueImg(item.ImageUrl)} alt=""
              style={{ display: 'block', width: '100%', maxHeight: 180, objectFit: 'cover' }}/>
          </div>
        )}
        <div style={{
          fontSize: 13, fontWeight: 600, marginBottom: 4, color: NT.ink,
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <span>{item.Question.TranslatedText.text}</span>
          {ref.Mandatory === 'Yes' && <span style={{ color: NT.danger, fontSize: 13 }}>*</span>}
          {isCalc && <NMChip color="calc" icon={<NI.Calc/>}>auto</NMChip>}
        </div>
        {item.Description && (
          <div style={{ fontSize: 12, color: NT.ink3, marginBottom: 6 }}>{item.Description}</div>
        )}
        {input}
        {(() => {
          const hasRules = !!(item.RangeCheck?.length || (item as any).Validations || (item as any).Validation || ref.Mandatory === 'Yes');
          if (!hasRules) return null;
          const legacyV: any = (item as any).Validations ?? (item as any).Validation ?? {};
          const validations: Record<string, unknown> = {
            ...legacyV,
            required: ref.Mandatory === 'Yes' ? true : legacyV.required,
            ...(item.RangeCheck?.length ? { _rangeChecks: item.RangeCheck } : {}),
          };
          const numericWithValue = NUMERIC_TYPES.has(item.DataType as string) && value !== null && value !== '' && value !== undefined;
          if (!numericWithValue && !touched[tk] && !submitted) return null;
          return <ValidationError value={value} validations={validations} dataType={item.DataType}/>;
        })()}
      </div>
    );
  };

  const renderGroupContent = (
    group: ItemGroupDef,
    instPrefix?: number,
    showOnFilter?: 'Normal' | 'Summarize',
  ): React.ReactNode => {
    const visibleRefs = showOnFilter === 'Summarize'
      ? group.ItemRef.filter(r => { const s = (r as any).ShowOn; return s === 'Summarize' || !s; })
      : showOnFilter === 'Normal'
      ? group.ItemRef.filter(r => { const s = (r as any).ShowOn; return s === 'Normal' || !s; })
      : group.ItemRef;
    return (
    <Fragment>
      {visibleRefs.map((ref) =>
        renderItem(itemByOid[ref.ItemOID], ref, group.OID, instPrefix),
      )}
     {(group.Subgroups ?? []).map((sg) => {
     const sgCondOid = (sg as any).ConditionOID 
     ?? (sg as any).CollectionExceptionConditionOID
     ?? group.ItemRef?.find((r: any) => r.ItemOID === sg.OID)?.CollectionExceptionConditionOID;
     console.log('[subgroup]', sg.OID, sg.Name, '| condOid:', sgCondOid, '| sg keys:', Object.keys(sg));
     if (isHidden(sgCondOid)) return null;
        const keyBase = sg.OID + (instPrefix !== undefined ? `@${instPrefix}` : '');

        if (sg.Repeating === 'Yes') {
          let subRepeat = 0;
          const sgRepeatFromOID: string | undefined = (sg as any).RepeatFromOID;
          const sgMethodOID: string | undefined = (sg as any).MethodOID;

          if (sgRepeatFromOID) {
            const raw = instPrefix !== undefined
              ? (answers[`${sgRepeatFromOID}@${instPrefix}`] ?? answers[sgRepeatFromOID])
              : answers[sgRepeatFromOID];
            subRepeat = Math.max(0, Math.min(50, parseInt(String(raw ?? ''), 10) || 0));
          } else if (sgMethodOID) {
            const instCtx: AnswerMap = {};
            for (const [k, v] of Object.entries(answers)) {
              if (instPrefix !== undefined && k.endsWith(`@${instPrefix}`)) {
                instCtx[k.slice(0, -(String(instPrefix).length + 1))] = v;
              } else if (!k.includes('@')) {
                instCtx[k] = v;
              }
            }
            const countVal = evalExpr(methByOid[sgMethodOID]?.FormalExpression?.text ?? '', instCtx);
            subRepeat = Math.max(0, Math.min(50, parseInt(String(countVal ?? ''), 10) || 0));
          }

          if (subRepeat === 0) {
            return null;
          }

          const canSummarizeSg = (sg as any).SummarizeInstances === 'Yes';
          const isSummarizedSg = canSummarizeSg && !!summarizeMode[sg.OID];

          return (
            <div key={keyBase} style={{ marginTop: 8 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: NT.ink2,
                  textTransform: 'uppercase', letterSpacing: 0.4, flex: 1,
                }}>{sg.Name}</div>
                {canSummarizeSg && (
                  <MSwitch
                    checked={isSummarizedSg}
                    onChange={v => setSummarizeMode(prev => ({ ...prev, [sg.OID]: v }))}
                    label="Summarize"
                  />
                )}
              </div>
              {isSummarizedSg ? (
                <div style={{
                  background: NT.surfaceAlt, padding: 10, borderRadius: 6,
                  marginBottom: 6, paddingLeft: 12, borderLeft: `2px solid ${NT.primary}`,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.5, color: NT.primary, marginBottom: 6,
                  }}>{sg.Name} — Summary</div>
                  {renderGroupContent(sg, instPrefix, 'Summarize')}
                </div>
              ) : (
                Array.from({ length: subRepeat }).map((_, i) => (
                  <div key={i} style={{
                    background: NT.surfaceAlt, padding: 10, borderRadius: 6,
                    marginBottom: 6, paddingLeft: 12, borderLeft: `2px solid ${NT.primary}`,
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.5, color: NT.primary, marginBottom: 6,
                    }}>{sg.Name.replace(/s?$/, '')} #{i + 1}</div>
                    {renderGroupContent(sg, i, canSummarizeSg ? 'Normal' : undefined)}
                  </div>
                ))
              )}
            </div>
          );
        }

        return (
          <div key={keyBase}
            style={{ marginTop: 10, paddingLeft: 12, borderLeft: `2px solid ${NT.line}` }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: NT.ink2, marginBottom: 8,
              textTransform: 'uppercase', letterSpacing: 0.4,
            }}>{sg.Name}</div>
            {renderGroupContent(sg, instPrefix)}
          </div>
        );
      })}
    </Fragment>
    );
  };

  return (
    <div className="naomi-scroll" style={{ padding: dens.formPad, overflowY: 'auto', height: '100%' }}>
      {tv.EventDef[0].FormRef.map((fr) => {
        const form = formByOid[fr.FormOID];
        if (!form) return null;
        return (
          <div key={form.OID} style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 15, fontWeight: 700, color: NT.ink, marginBottom: 4,
              paddingBottom: 8, borderBottom: `1px solid ${NT.line}`,
            }}>{form.Name}</div>
            {form.ItemGroupRef.map((gr) => {
              const group = groupByOid[gr.ItemGroupOID];
              if (!group) return null;
              if (isHidden(gr.CollectionExceptionConditionOID)) return null;

              let repeat = group.Repeating === 'Yes' ? 0 : 1;
              if (gr.MethodOID) {
                const countOid = extractRepeatCountOid(gr.MethodOID);
                if (!countOid || !isItemHidden(countOid)) {
                  const v = computeMethod(gr.MethodOID);
                  repeat = Math.max(0, Math.min(50, parseInt(String(v ?? ''), 10) || 0));
                }
              } else if ((group as any).RepeatFromOID) {
                const countOid = (group as any).RepeatFromOID as OID;
                if (!isItemHidden(countOid)) {
                  const raw = answers[countOid];
                  repeat = Math.max(0, Math.min(50, parseInt(String(raw ?? ''), 10) || 0));
                }
              }

              if (group.Repeating === 'Yes') {
                const canSummarize = (gr as any).SummarizeInstances === 'Yes';
                const isSummarized = canSummarize && !!summarizeMode[group.OID];

                if (repeat === 0) {
                  return null;
                }
                return (
                  <div key={group.OID} style={{ marginTop: dens.gap }}>
                    {globals.renderGroupHeaderBanner?.(group)}
                    {canSummarize && (
                      <MSwitch
                        checked={isSummarized}
                        onChange={v => setSummarizeMode(prev => ({ ...prev, [group.OID]: v }))}
                        label="Summarize"
                      />
                    )}
                    {isSummarized ? (
                      <div style={{ background: NT.surfaceAlt, padding: 12, borderRadius: 8, marginBottom: 8 }}>
                        <div style={{
                          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: 0.5, color: NT.primary, marginBottom: 8,
                        }}>{group.Name} — Summary</div>
                        {renderGroupContent(group, undefined, 'Summarize')}
                      </div>
                    ) : (
                      Array.from({ length: repeat }).map((_, i) => (
                        <div key={i}
                          style={{ background: NT.surfaceAlt, padding: 12, borderRadius: 8, marginBottom: 8 }}>
                          <div style={{
                            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: 0.5, color: NT.primary, marginBottom: 8,
                          }}>{group.Name.replace(/s?$/, '')} #{i + 1}</div>
                          {renderGroupContent(group, i, canSummarize ? 'Normal' : undefined)}
                        </div>
                      ))
                    )}
                  </div>
                );
              }

              return (
                <div key={group.OID} style={{ marginTop: dens.gap }}>
                  {globals.renderGroupHeaderBanner?.(group)}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 12.5, fontWeight: 700, color: NT.ink2,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    paddingBottom: 6, marginBottom: 10,
                    borderBottom: `1px solid ${NT.line}`,
                  }}>
                    <span style={{ color: NT.ink4, display: 'inline-flex' }}><NI.Group/></span>
                    <span>{group.Name}</span>
                  </div>
                  {renderGroupContent(group)}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

Object.assign(window, { useSDMState, LivePreview, BranchBadge, CalcBadge, TypeMeta });
